import { A } from '@ember/array';
import { array, concat } from '@ember/helper';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { LinkTo } from '@ember/routing';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template/-private/handlebars';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { findBy } from '@nullvoxpopuli/ember-composable-helpers';
import { dropTask, timeout } from 'ember-concurrency';
import { pluralize } from 'ember-inflector';
import round from 'ember-math-helpers/helpers/round';
import set from 'ember-set-helper/helpers/set';
import { eq } from 'ember-truth-helpers';
import SortableColumnHeader from 'garaje/components/sortable-column-header';
import UiHeader from 'garaje/components/ui/header';
import dateFormat from 'garaje/helpers/date-format';
import type AverageMonthlyVisitorCountModel from 'garaje/models/average-monthly-visitor-count';
import type LocationModel from 'garaje/models/location';
import type { ModelData } from 'garaje/utils/type-utils';
const SORT_KEYS = {
  'Location name': 'name',
  'Location created': 'createdAt',
  'Last sign-in': 'lastEntryAt',
  'Average monthly visitors': 'averageMonthlyVisitorCount',
} as const;

interface LocationsTableSignature {
  Args: {
    locations: LocationModel[];
    averageMonthlyVisitorCount: AverageMonthlyVisitorCountModel[];
  };
}

type LocationWithAverageCount = ModelData<LocationModel> & {
  averageMonthlyVisitorCount: number;
  id: string;
};

export default class LocationsTable extends Component<LocationsTableSignature> {
  @tracked sortBy: keyof typeof SORT_KEYS = 'Location name';
  @tracked sortDirection = 'A-Z';
  @tracked fixHeader = false;
  @tracked selectedLocations: LocationModel[] = [];
  @tracked resizeListener;
  @tracked scrollListener;
  @tracked declare element: HTMLDivElement;

  constructor(owner: unknown, args: LocationsTableSignature['Args']) {
    super(owner, args);
    this.selectedLocations = [];

    this.resizeListener = () => {
      void this.resizeColsTask.perform();
    };

    this.scrollListener = () => {
      void this.adjustHeaderStylesTask.perform();
    };

    window.addEventListener('resize', this.resizeListener);
    window.addEventListener('scroll', this.scrollListener);
  }

  get locationsWithAverageCount(): LocationWithAverageCount[] {
    const locations = this.args.locations;
    const averageMonthlyVisitorCounts = this.args.averageMonthlyVisitorCount;

    return locations.map((location) => {
      const countObject: Partial<AverageMonthlyVisitorCountModel> =
        averageMonthlyVisitorCounts.find((a) => {
          return a.belongsTo('location').id() === location.id;
        }) || {};

      return {
        ...location.toJSON(),
        id: location.id,
        averageMonthlyVisitorCount: countObject.count || 0,
      };
    });
  }

  get headerStyles(): SafeString {
    let style = ``;

    if (this.fixHeader) {
      style = `
          position: fixed;
          top: 0;
          box-shadow: 0 6px 12px 0 #e9e9ea;
        `;
    }

    return htmlSafe(style);
  }

  get isSortAsc(): boolean {
    const sortAscKeys = ['A-Z', 'Least to most recent', 'Oldest to newest', 'Fewest to most'];
    return sortAscKeys.includes(this.sortDirection);
  }

  get sortedLocations(): LocationWithAverageCount[] {
    const sortBy = SORT_KEYS[this.sortBy];
    let locations = A(this.locationsWithAverageCount).sortBy(sortBy).slice();

    if (!this.isSortAsc) {
      locations = locations.reverse();
    }

    return locations;
  }

  @action
  onInsert(element: HTMLDivElement): void {
    this.element = element;
    void this.resizeColsTask.perform();
  }

  willDestroy(...args: Parameters<typeof Component.prototype.willDestroy>): void {
    super.willDestroy(...args);
    window.removeEventListener('resize', this.resizeListener);
    window.removeEventListener('scroll', this.scrollListener);
  }

  /*
   * resizeColsTask
   *
   * In order to acheive the floating cols as the talble scrolls, we
   * have a dispaly table ([data-display-table]) and hide actual
   * `thead`. This task syncs with column width between the display
   * table and actual table to simulate the native content stretch
   * that occurs in an HTML table.
   */
  resizeColsTask = dropTask(async () => {
    await timeout(100);
    const tableCols = this.element.querySelectorAll('[data-display-table] tr:first-child td');
    const tableColWidths = [...tableCols].map((col) => col.clientWidth);
    const tableThs = this.element.querySelectorAll<HTMLTableCellElement>('[data-header-table] th');

    tableThs.forEach((col, index) => {
      col.style.width = `${tableColWidths[index]}px`;
    });
  });

  adjustHeaderStylesTask = dropTask(async () => {
    await timeout(10);

    const html = document.querySelector('html');
    const contentContainer = document.querySelector<HTMLDivElement>('.contentContainer');

    if (html && contentContainer) {
      this.fixHeader = html.scrollTop > contentContainer.offsetTop;
    }
  });

  @action
  toggleSelectLocation(location: LocationModel): void {
    const locations = this.selectedLocations;
    const locationId = location.id;

    if (locations.find((loc) => loc.id === locationId)) {
      const indexOfElementToRemove = locations.findIndex((location) => location.id === locationId);
      this.selectedLocations = [
        ...locations.slice(0, indexOfElementToRemove),
        ...locations.slice(indexOfElementToRemove + 1),
      ];
    } else {
      this.selectedLocations = [...locations, location];
    }
  }

  @action
  toggleAllLocations(): void {
    const selectedLocations = this.selectedLocations;
    const locations = this.args.locations;

    if (selectedLocations.length === locations.length) {
      this.selectedLocations = [];
    } else {
      this.selectedLocations = [...locations];
    }
  }

  <template>
    <div {{didInsert this.onInsert}} class="space-y-8">
      <div class="flex items-center">
        <UiHeader class="flex-auto">
          <span data-test-location-overview-heading>Manage locations</span>:
          {{pluralize @locations.length "location"}}
        </UiHeader>
        <LinkTo @route="new-location" class="btn btn--small btn--brand" data-test-new-location>
          New location
        </LinkTo>
      </div>

      <table data-header-table aria-hidden="true">
        <thead>
          <tr class="uppercase text-xs" data-test-location-overview-cols>
            <th class="py-2">
              <SortableColumnHeader
                @sortBy="Location name"
                @sortDirection={{this.sortDirection}}
                @isActive={{eq this.sortBy "Location name"}}
                @setSortDirection={{set this "sortDirection"}}
                @setSortBy={{set this "sortBy"}}
                @sortOptions={{array "A-Z" "Z-A"}}
              />
            </th>
            <th class="py-2">
              <SortableColumnHeader
                @sortBy="Location created"
                @sortDirection={{this.sortDirection}}
                @isActive={{eq this.sortBy "Location created"}}
                @setSortDirection={{set this "sortDirection"}}
                @setSortBy={{set this "sortBy"}}
                @sortOptions={{array "Newest to oldest" "Oldest to newest"}}
              />
            </th>
            <th class="py-2">
              <SortableColumnHeader
                @sortBy="Last sign-in"
                @sortDirection={{this.sortDirection}}
                @isActive={{eq this.sortBy "Last sign-in"}}
                @setSortDirection={{set this "sortDirection"}}
                @setSortBy={{set this "sortBy"}}
                @sortOptions={{array "Most to least recent" "Least to most recent"}}
              />
            </th>
            <th class="py-2">
              <SortableColumnHeader
                @sortBy="Average monthly visitors"
                @sortDirection={{this.sortDirection}}
                @isActive={{eq this.sortBy "Average monthly visitors"}}
                @setSortDirection={{set this "sortDirection"}}
                @setSortBy={{set this "sortBy"}}
                @sortOptions={{array "Most to fewest" "Fewest to most"}}
              />
            </th>
          </tr>
        </thead>
      </table>

      <div class="!mt-0">
        <table class="border-t" data-display-table>
          {{! hidden here for table a11y. Done above for floating header }}
          <thead class="sr-only">
            <tr class="uppercase text-xs" data-test-location-overview-cols>
              <th>
                &nbsp;
              </th>
              <th>
                Location name
              </th>
              <th>
                Location created
              </th>
              <th>
                Last sign-in
              </th>
              <th>
                Average monthly visitors
              </th>
            </tr>
          </thead>
          <tbody>
            {{#each this.sortedLocations as |location|}}
              {{#let (findBy "id" location.id this.selectedLocations) as |isActive|}}
                {{#let (concat "border-b py-2 align-middle" (if isActive " bg-carbon-5")) as |classNames|}}
                  <tr style="height: 55px; width: 5%" data-test-location-row>
                    <td class={{classNames}} style="max-width: 170px; width: 25%" data-test-name>
                      {{location.name}}
                    </td>
                    <td class={{classNames}} data-test-created-at style="width: 25%">
                      {{dateFormat location.createdAt "MMM d, yyyy"}}
                    </td>
                    <td class={{classNames}} data-test-last-signed-in style="width: 25%">
                      {{#if location.lastEntryAt}}
                        {{dateFormat location.lastEntryAt "MMM d, yyyy"}}
                      {{else}}
                        None
                      {{/if}}
                    </td>
                    <td class={{classNames}} style="min-width: 170px; width: 20%" data-test-average-monthly-visitors>
                      {{round location.averageMonthlyVisitorCount}}
                    </td>
                  </tr>
                {{/let}}
              {{/let}}
            {{/each}}
          </tbody>
        </table>
        <div class="mt-8 text-xs font-bold uppercase text-carbon-20" data-test-footer>
          <span data-test-location-overview-footer>
            {{pluralize @locations.length "location"}}
          </span>
        </div>
      </div>
    </div>
  </template>
}
